import {assert} from 'Asserts';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {DeletePipelineStepsBehavior} from 'pipelines/PipelineDAO';
import * as PipelineDAO from 'pipelines/PipelineDAO';
import {PipelineGraphQL} from 'pipelines/PipelineTypes';
import {getLatestStepVersion, getSourceStepTypes} from 'pipelines/StepConnectionUtils';

import {AnalysisFlowchartAction} from '../AnalysisFlowchartActionTypes';

type Pipeline = Pick<PipelineGraphQL, 'steps' | 'id'>;

/**
 * Add a new source step to the pipeline.
 *
 * @returns {stepId} ID of the newly added step.
 */
async function addSourceStep(pipeline: Pipeline, stepType: PipelineStepType): Promise<{stepId: string}> {
  assert(getSourceStepTypes().includes(stepType), 'Tried to add a non-source step type');
  const {newStepIds} = await PipelineDAO.addAndUpdatePipelineSteps(pipeline.id, {
    stepsToAdd: [{stepType, stepVersion: getLatestStepVersion(stepType), incomingEdges: []}],
    stepsToUpdate: [],
  });

  return {stepId: newStepIds[0]};
}

/** Pass through the return value to the parent of <AnalysisFlowchart>. */
const openAddStep = (
  pipeline: Pipeline,
  stepId: string,
  stepDataName: string
): Promise<{stepId: string; stepDataName: string}> => Promise.resolve({stepId, stepDataName});

/**
 * Call shared `pipelines:` endpoint to delete a step, and attempt to re-parent it.
 */
async function deleteStep(pipeline: Pipeline, stepId: string): Promise<void> {
  await PipelineDAO.deletePipelineSteps(pipeline.id, {
    stepIds: [stepId],
    behavior: DeletePipelineStepsBehavior.NO_INFERENCE_WITH_REPARENTING,
  });
}

/**
 * Call shared `pipelines:` endpoint to delete a step, and its descendents.
 */
async function deleteStepAndChildren(pipeline: Pipeline, stepId: string): Promise<void> {
  await PipelineDAO.deletePipelineSteps(pipeline.id, {
    stepIds: [stepId],
    behavior: DeletePipelineStepsBehavior.DELETE_ALL_CHILDREN,
  });
}

async function runStep(pipeline: Pipeline, stepId: string): Promise<void> {
  await PipelineDAO.runPipelineStep(pipeline.id, {stepId});
}

/** Pass through the return value to the parent of <AnalysisFlowchart>. */
const openStepConfiguration = (pipeline: Pipeline, stepId: string): Promise<{stepId: string}> =>
  Promise.resolve({stepId});

/**
 * Logic to execute each `AnalysisFlowchartAction`.
 *
 * The `onAction` callback (provided by the parent of <AnalysisFlowchart> in `AnalysisFlowchartActionInfo`)
 * is called after these internal functions complete.
 */
export const INTERNAL_FLOWCHART_ACTION_FUNCTIONS = {
  [AnalysisFlowchartAction.ADD_SOURCE_STEP]: addSourceStep,
  [AnalysisFlowchartAction.DELETE_STEP]: deleteStep,
  [AnalysisFlowchartAction.DELETE_STEP_AND_CHILDREN]: deleteStepAndChildren,
  [AnalysisFlowchartAction.OPEN_ADD_STEP]: openAddStep,
  [AnalysisFlowchartAction.OPEN_STEP_CONFIGURATION]: openStepConfiguration,
  [AnalysisFlowchartAction.RERUN_STEP]: runStep,
} satisfies Record<
  AnalysisFlowchartAction,
  (pipeline: Pipeline, stepId: string, ...args: Array<unknown>) => Promise<unknown>
>;

/**
 * True if the action function doesn't require a GraphQL refresh or loading screen.
 */
export const INTERNAL_FLOWCHART_ACTION_SKIP_REFRESH: Partial<Record<AnalysisFlowchartAction, boolean>> = {
  [AnalysisFlowchartAction.OPEN_ADD_STEP]: true,
  [AnalysisFlowchartAction.OPEN_STEP_CONFIGURATION]: true,
};
